import { Box, Typography } from '@mui/material'
import TaAddElementsButton from './TaAddElementsButton'
import TAChip from './TAChip'
import { useAppActions } from '../AppContext'
import { FS_SM } from '../common/theme'
import { scoreTypeLabel } from '../common/common'
import type { Question } from '../types'

interface TaElementsProps {
  // The question driving the row. Nullable so consumers like
  // ScoreFixTab can mount this before a question is selected — when
  // null the row renders nothing rather than crashing on the bideval
  // dereference.
  question: Question | null | undefined
  // Which slice of the question this row is editing. Drives both the
  // add-button modal mode and the chip field this row reads / writes:
  //   • 'structure' → question.structuretweaks
  //   • 'answer'    → question.answertweaks
  //   • 'scoring'   → question.bideval.scoringelements
  mode: 'structure' | 'answer' | 'scoring'
  disabled?: boolean
  testId?: string
  // Optional override for the chip list (defaults to the matching
  // field on the question).
  elements?: any[]
}

// Strip HTML tags from a string so a chip label stays single-line and
// safe.
function toPlainText(html?: string | null): string {
  if (!html) return ''
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '')
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent ?? '').trim()
}

// New stored shape is { id, name }. Older / raw shapes used
// tagnamehtml / tagname / valuehtml — fall back through those so
// chips still label sensibly during the transition.
function tagLabel(e: any): string {
  return e?.name || toPlainText(e?.tagnamehtml) || e?.tagname || toPlainText(e?.valuehtml) || '(unnamed)'
}

// Unified "elements" row — the add-button and the existing chips for
// one of the three question element fields, laid out in a single flex
// row so chips flow inline with the add button. Replaces the earlier
// `<TaAddElementsButton /><TaTweaks />` pair (each of which had its
// own flex wrapper).
//
// In `scoring` mode the row also leads with the current score-type
// label and gates the add button when the type isn't 'scored' (the
// other types don't take scoring elements).
export default function TaElements({
  question, mode, disabled, testId, elements,
}: TaElementsProps) {
  const { updateQuestion } = useAppActions()

  // No question yet (e.g. ScoreFixTab opened before a question is
  // selected) → render nothing rather than crash on `bideval` access.
  if (!question) return null

  const scoreType = (question.bideval as any)?.scoretypestring
  const scoringDisabled = mode === 'scoring' && scoreType !== 'scored'

  const defaultList =
    mode === 'structure' ? (question.structuretweaks ?? []) :
      mode === 'answer' ? ((question as any).answertweaks ?? []) :
        ((question.bideval as any)?.scoringelements ?? [])
  const list = elements ?? defaultList

  const handleRemove = (i: number) => {
    const updated = [...list]
    updated.splice(i, 1)
    if (mode === 'structure') {
      updateQuestion(question.questionsha, { structuretweaks: updated })
    } else if (mode === 'answer') {
      updateQuestion(question.questionsha, { answertweaks: updated } as any)
    } else {
      const prevBideval = (question.bideval as any) ?? {}
      updateQuestion(question.questionsha, {
        bideval: { ...prevBideval, scoringelements: updated },
      })
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {mode === 'scoring' && (
        <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 500, pl: 0.5 }}>
          {scoreTypeLabel(scoreType)}
        </Typography>
      )}
      <TaAddElementsButton
        mode={mode}
        question={question}
        disabled={disabled || scoringDisabled}
        testId={testId}
      />
      {list.map((e: any, i: number) => (
        <TAChip
          key={e.tagsha ?? e.id ?? i}
          kind={mode === 'scoring' ? 'scoretemplate' : 'answertweak'}
          label={tagLabel(e)}
          onDelete={() => handleRemove(i)}
        />
      ))}
    </Box>
  )
}
